import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BardoContextDataService,
  ClienteService,
  EstabelecimentoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  BottomSheetComponent,
  DeviceService,
  NMultiselectComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  IClienteDependenteEntity,
  IEstabelecimentoClienteEntity,
} from '@nomoh-monorepo/shared-types';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-cliente-vinculos',
  standalone: true,
  templateUrl: './cliente-vinculos.component.html',
  styleUrls: ['./cliente-vinculos.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    TooltipModule,
    BottomSheetComponent,
    NMultiselectComponent,
    TranslateModule,
  ],
})
export class ClienteVinculosComponent implements OnChanges {
  @Input() cliente: IEstabelecimentoClienteEntity =
    {} as IEstabelecimentoClienteEntity;
  @Output() vinculoAlterado = new EventEmitter<void>();

  private clienteService = inject(ClienteService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private confirmationService = inject(ConfirmationService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  deviceService = inject(DeviceService);
  private contextoService = inject(BardoContextDataService);

  private avatarPalette = [
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#F97316',
    '#EA580C',
  ];

  // Signals de Vínculos
  $dependentes = signal<IClienteDependenteEntity[]>([]);
  $responsaveis = signal<IClienteDependenteEntity[]>([]);
  $carregandoVinculos = signal<boolean>(false);

  // Modal Novo Vínculo
  modalVinculoAberto = signal<boolean>(false);
  tipoVinculo = signal<'dependente' | 'responsavel'>('dependente');
  clienteSelecionadoParaVinculoId = signal<string>('');
  opcoesOutrosClientes = signal<Array<{ label: string; value: string }>>([]);
  carregandoClientesParaVinculo = signal<boolean>(false);
  salvandoVinculo = signal<boolean>(false);

  // Modal Confirmação de Remoção de Vínculo (Mobile)
  modalConfirmacaoRemocaoAberto = signal<boolean>(false);
  vinculoParaRemover = signal<{ id: string; nome: string } | null>(null);
  removendoVinculo = signal<boolean>(false);

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['cliente']?.currentValue?.id) {
      await this.carregarVinculos();
    }
  }

  possuiPermissao(permissao: string): boolean {
    return this.contextoService.possuiPermissao(permissao);
  }

  private get estabelecimentoId(): string | undefined {
    return (
      this.estabelecimentoDataService.$estabelecimentoAtual()?.id ||
      this.contextoService.$contextoAtivo()?.contextoId
    );
  }

  async carregarVinculos(): Promise<void> {
    const estId = this.estabelecimentoId;
    if (!this.cliente?.id || !estId) return;

    this.$carregandoVinculos.set(true);
    try {
      const [deps, resps] = await Promise.all([
        this.clienteService.findDependentes(this.cliente.id, estId),
        this.clienteService.findResponsaveis(this.cliente.id, estId),
      ]);
      this.$dependentes.set(deps);
      this.$responsaveis.set(resps);
    } catch (error) {
      console.error('Erro ao carregar vínculos do cliente:', error);
    } finally {
      this.$carregandoVinculos.set(false);
    }
  }

  get clientePossuiTelefone(): boolean {
    return !!this.cliente?.telefone && this.cliente.telefone.trim().length > 0;
  }

  async abrirModalAdicionarVinculo(): Promise<void> {
    const estId = this.estabelecimentoId;
    if (!estId || !this.cliente?.id) return;

    // Se este cliente não possui telefone, obrigatoriamente só pode receber um responsável por ele
    if (!this.clientePossuiTelefone) {
      this.tipoVinculo.set('responsavel');
    } else {
      this.tipoVinculo.set('dependente');
    }

    this.clienteSelecionadoParaVinculoId.set('');
    this.modalVinculoAberto.set(true);
    await this.carregarOpcoesOutrosClientes();
  }

  async onTipoVinculoChange(tipo: 'dependente' | 'responsavel'): Promise<void> {
    if (tipo === 'dependente' && !this.clientePossuiTelefone) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.AVISO_SEM_TELEFONE')
      );
      this.tipoVinculo.set('responsavel');
      return;
    }

    this.tipoVinculo.set(tipo);
    this.clienteSelecionadoParaVinculoId.set('');
    await this.carregarOpcoesOutrosClientes();
  }

  async carregarOpcoesOutrosClientes(): Promise<void> {
    const estId = this.estabelecimentoId;
    if (!estId || !this.cliente?.id) return;

    this.carregandoClientesParaVinculo.set(true);
    try {
      const res = await this.clienteService.findAllPaginado(estId, 1, 200);
      let outros = (res?.data || []).filter(
        (c): c is IEstabelecimentoClienteEntity & { id: string } =>
          !!c.id && c.id !== this.cliente.id
      );

      // Se estamos buscando um RESPONSÁVEL para este cliente, apenas clientes com telefone podem ser selecionados!
      if (this.tipoVinculo() === 'responsavel') {
        outros = outros.filter(
          (c) => !!c.telefone && c.telefone.trim().length > 0
        );
      }

      this.opcoesOutrosClientes.set(
        outros.map((c) => ({
          label: `${c.nome}${c.telefone ? ' (' + formatarTelefone(c.telefone) + ')' : ''}`,
          value: c.id,
        }))
      );
    } catch (error) {
      console.error('Erro ao carregar clientes para vínculo:', error);
    } finally {
      this.carregandoClientesParaVinculo.set(false);
    }
  }


  fecharModalVinculo(): void {
    this.modalVinculoAberto.set(false);
  }

  async salvarVinculo(): Promise<void> {
    const estId = this.estabelecimentoId;
    const outroId = this.clienteSelecionadoParaVinculoId();

    if (!estId || !this.cliente?.id || !outroId) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.SELECIONE_CLIENTE_AVISO')
      );
      return;
    }

    this.salvandoVinculo.set(true);
    try {
      const dto =
        this.tipoVinculo() === 'dependente'
          ? {
            estabelecimentoId: estId,
            responsavelId: this.cliente.id,
            clienteId: outroId,
            contatoPrincipal: true,
          }
          : {
            estabelecimentoId: estId,
            responsavelId: outroId,
            clienteId: this.cliente.id,
            contatoPrincipal: true,
          };

      await this.clienteService.criarVinculo(dto);
      this.notificacaoService.notificacaoSuccess(
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.CRIADO_SUCESSO')
      );
      this.fecharModalVinculo();
      await this.carregarVinculos();
      this.vinculoAlterado.emit();
    } catch (error) {
      const msg =
        (error as { error?: { message?: string } })?.error?.message ||
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.CRIAR_ERRO');
      this.notificacaoService.notificacaoError(msg);
    } finally {
      this.salvandoVinculo.set(false);
    }
  }

  confirmarRemocaoVinculo(
    vinculo: IClienteDependenteEntity,
    nomeOutro: string,
    event: Event
  ): void {
    event.stopPropagation();
    const estId = this.estabelecimentoId;
    const vinculoId = vinculo.id;
    if (!vinculoId || !estId) return;

    if (this.deviceService.$isMobile()) {
      this.vinculoParaRemover.set({ id: vinculoId, nome: nomeOutro });
      this.modalConfirmacaoRemocaoAberto.set(true);
    } else {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: this.translateService.instant('BARDO_CLIENTES.VINCULOS.CONFIRMAR_REMOCAO_MSG', { nome: nomeOutro }),
        header: this.translateService.instant('BARDO_CLIENTES.VINCULOS.CONFIRMAR_REMOCAO_TITULO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_REMOVER'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.removerVinculo(vinculoId, estId),
      });
    }
  }

  async confirmarRemocaoConfirmadaMobile(): Promise<void> {
    const vinculo = this.vinculoParaRemover();
    const estId = this.estabelecimentoId;
    if (vinculo?.id && estId) {
      this.removendoVinculo.set(true);
      try {
        await this.removerVinculo(vinculo.id, estId);
        this.modalConfirmacaoRemocaoAberto.set(false);
        this.vinculoParaRemover.set(null);
      } finally {
        this.removendoVinculo.set(false);
      }
    }
  }

  private async removerVinculo(
    vinculoId: string,
    estabelecimentoId: string
  ): Promise<void> {
    try {
      await this.clienteService.removerVinculo(vinculoId, estabelecimentoId);
      this.notificacaoService.notificacaoSuccess(
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.REMOVIDO_SUCESSO')
      );
      await this.carregarVinculos();
      this.vinculoAlterado.emit();
    } catch (error) {
      const msg =
        (error as { error?: { message?: string } })?.error?.message ||
        this.translateService.instant('BARDO_CLIENTES.VINCULOS.REMOVER_ERRO');
      this.notificacaoService.notificacaoError(msg);
    }
  }

  getAvatarColor(id?: string): string {
    if (!id) return this.avatarPalette[0];
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    return this.avatarPalette[Math.abs(hash) % this.avatarPalette.length];
  }

  getIniciais(nome?: string | null): string {
    if (!nome) return '';
    return nome
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getPrimeiroNome(nome?: string | null): string {
    if (!nome) return '';
    return nome.trim().split(' ')[0] || '';
  }

  formatarTelefone(telefone?: string | null): string {
    if (!telefone) {
      return this.translateService.instant('BARDO_CLIENTES.VINCULOS.NAO_INFORMADO');
    }
    return formatarTelefone(telefone);
  }
}
